import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { JiraSourceSettingsApi } from "./JiraSourceSettings.Api";
import type { JiraSourceSettingsViewModel } from "./JiraSourceSettings.ViewModel.types";
import type {
  JiraConfiguration,
  JiraIssueType,
  JiraProject,
  JiraResource,
  JiraStatus,
} from "./JiraSourceSettings.types";

const NONE_OPTION = "__none";

const normalizeId = (value: unknown): string => String(value ?? "");

const getDoneDefaultStatus = (
  statuses: JiraStatus[],
): JiraStatus | undefined => {
  let doneStatus: JiraStatus | undefined;
  statuses.forEach((status) => {
    if (status.statusCategory?.key === "done") {
      doneStatus = status;
    }
  });

  return doneStatus;
};

export const useJiraSourceSettingsViewModel = (
  model: SourceSettingsModel,
): JiraSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const autoSelectedResourceForConnectionRef = useRef<string | null>(null);
  const seededStatusesForResourceRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [resources, setResources] = useState<JiraResource[]>([]);
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [issueTypes, setIssueTypes] = useState<JiraIssueType[]>([]);
  const [statuses, setStatuses] = useState<JiraStatus[]>([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [issueTypesLoaded, setIssueTypesLoaded] = useState(false);
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Jira && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<JiraConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );
  const selectedResourceId = normalizeId(configuration.resource?.id);

  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      autoSelectedResourceForConnectionRef.current = null;
      seededStatusesForResourceRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<JiraConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: cfg.projects ?? [],
        statusesToLoad: cfg.statusesToLoad ?? [],
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setProjects([]);
    setIssueTypes([]);
    setStatuses([]);
    autoSelectedResourceForConnectionRef.current = null;
    seededStatusesForResourceRef.current = null;

    updateConnectionConfiguration<JiraConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        projects: [],
        issueTypes: [],
        doneStatus: undefined,
        statusesToLoad: [],
        resource: undefined,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadResources = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setResources([]);
        setResourcesLoaded(true);
        return;
      }

      const jiraApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<JiraSourceSettingsApi>(
          SourceTypeIds.Jira,
        );
      if (!jiraApi) {
        setResources([]);
        setResourcesLoaded(true);
        return;
      }

      setResourcesLoaded(false);
      setHasError(false);
      try {
        const nextResources = await jiraApi.getResources(
          model.boardId,
          editingConnectionId,
        );
        setResources(nextResources);

        if (
          !selectedResourceId &&
          nextResources.length > 0 &&
          autoSelectedResourceForConnectionRef.current !== editingConnectionId
        ) {
          autoSelectedResourceForConnectionRef.current = editingConnectionId;
          updateConnectionConfiguration<JiraConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            (cfg) =>
              normalizeId(cfg.resource?.id)
                ? cfg
                : {
                    ...cfg,
                    resource: nextResources[0],
                    projects: [],
                    issueTypes: [],
                    doneStatus: undefined,
                    statusesToLoad: [],
                  },
            { isDefault: true },
          );
        }
      } catch {
        setResources([]);
        setHasError(true);
      } finally {
        setResourcesLoaded(true);
      }
    };

    void loadResources();
  }, [editingConnectionId, isVisible, model.boardId, selectedResourceId]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedResourceId
      ) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      const jiraApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<JiraSourceSettingsApi>(
          SourceTypeIds.Jira,
        );
      if (!jiraApi) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      setProjectsLoaded(false);
      setHasError(false);
      try {
        setProjects(
          await jiraApi.getProjects(
            model.boardId,
            editingConnectionId,
            selectedResourceId,
          ),
        );
      } catch {
        setProjects([]);
        setHasError(true);
      } finally {
        setProjectsLoaded(true);
      }
    };

    void loadProjects();
  }, [editingConnectionId, isVisible, model.boardId, selectedResourceId]);

  useEffect(() => {
    const loadIssueTypes = async (): Promise<void> => {
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedResourceId
      ) {
        setIssueTypes([]);
        setIssueTypesLoaded(true);
        return;
      }

      const jiraApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<JiraSourceSettingsApi>(
          SourceTypeIds.Jira,
        );
      if (!jiraApi) {
        setIssueTypes([]);
        setIssueTypesLoaded(true);
        return;
      }

      setIssueTypesLoaded(false);
      setHasError(false);
      try {
        setIssueTypes(
          await jiraApi.getIssueTypes(
            model.boardId,
            editingConnectionId,
            selectedResourceId,
          ),
        );
      } catch {
        setIssueTypes([]);
        setHasError(true);
      } finally {
        setIssueTypesLoaded(true);
      }
    };

    void loadIssueTypes();
  }, [editingConnectionId, isVisible, model.boardId, selectedResourceId]);

  useEffect(() => {
    const loadStatuses = async (): Promise<void> => {
      if (
        !isVisible ||
        !model.boardId ||
        !editingConnectionId ||
        !selectedResourceId
      ) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      const jiraApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<JiraSourceSettingsApi>(
          SourceTypeIds.Jira,
        );
      if (!jiraApi) {
        setStatuses([]);
        setStatusesLoaded(true);
        return;
      }

      setStatusesLoaded(false);
      setHasError(false);
      try {
        const nextStatuses = await jiraApi.getStatuses(
          model.boardId,
          editingConnectionId,
          selectedResourceId,
        );
        setStatuses(nextStatuses);

        const seedKey = `${editingConnectionId}:${selectedResourceId}`;
        if (
          (configuration.statusesToLoad?.length ?? 0) === 0 &&
          seededStatusesForResourceRef.current !== seedKey
        ) {
          seededStatusesForResourceRef.current = seedKey;
          const defaultStatusesToLoad = nextStatuses.filter(
            (status) => status.statusCategory?.key !== "done",
          );
          const defaultDoneStatus = getDoneDefaultStatus(nextStatuses);

          updateConnectionConfiguration<JiraConfiguration>(
            editingConnectionRef.current,
            onUpdateRef.current,
            // Seeds only the resource the statuses were loaded for, and only
            // what is missing, so it is safe to apply to the saved settings too.
            (cfg) =>
              normalizeId(cfg.resource?.id) !== selectedResourceId ||
              (cfg.statusesToLoad?.length ?? 0) > 0
                ? cfg
                : {
                    ...cfg,
                    statusesToLoad: defaultStatusesToLoad,
                    doneStatus: cfg.doneStatus ?? defaultDoneStatus,
                  },
            { isDefault: true },
          );
        }
      } catch {
        setStatuses([]);
        setHasError(true);
      } finally {
        setStatusesLoaded(true);
      }
    };

    void loadStatuses();
  }, [
    configuration.statusesToLoad?.length,
    editingConnectionId,
    isVisible,
    model.boardId,
    selectedResourceId,
  ]);

  const statusReferenceErrorText = useMemo(() => {
    if (!configuration.doneStatus || !configuration.statusesToLoad?.length) {
      return "";
    }

    return configuration.statusesToLoad.some(
      (status) =>
        normalizeId(status.id) === normalizeId(configuration.doneStatus?.id),
    )
      ? model.t(
          "board.connections.dialog.sourceSettings.jira.statusReferenceError",
        )
      : "";
  }, [configuration.doneStatus, configuration.statusesToLoad, model]);

  return {
    isVisible,
    resources,
    projects,
    issueTypes,
    statuses,
    resourcesLoaded,
    projectsLoaded,
    issueTypesLoaded,
    statusesLoaded,
    hasError,
    selectedResourceId: selectedResourceId || NONE_OPTION,
    selectedProjectsIds: (configuration.projects ?? []).map((project) =>
      normalizeId(project.id),
    ),
    selectedIssueTypeIds: (configuration.issueTypes ?? []).map((issueType) =>
      normalizeId(issueType.id),
    ),
    selectedStatusesToLoadIds: (configuration.statusesToLoad ?? []).map(
      (status) => normalizeId(status.id),
    ),
    selectedDoneStatusId:
      normalizeId(configuration.doneStatus?.id) || NONE_OPTION,
    includeProjectInTitle: Boolean(configuration.includeProjectInTitle),
    includeTypeAcronymInTitle: Boolean(configuration.includeTypeAcronymInTitle),
    statusReferenceErrorText,
    noneOptionValue: NONE_OPTION,
    assignedToMeFixed: true,
    onSelectResource: (resourceId?: string) => {
      const selectedId = String(resourceId || NONE_OPTION);
      const selectedResource = resources.find(
        (resource) => normalizeId(resource.id) === selectedId,
      );

      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          resource: selectedId === NONE_OPTION ? undefined : selectedResource,
          projects: [],
          issueTypes: [],
          doneStatus: undefined,
          statusesToLoad: [],
        }),
      );
    },
    onSelectProjects: (projectIds: string[]) => {
      const selected = projects.filter((project) =>
        projectIds.includes(normalizeId(project.id)),
      );

      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          projects: selected,
        }),
      );
    },
    onSelectIssueTypes: (issueTypeIds: string[]) => {
      const selected = issueTypes.filter((issueType) =>
        issueTypeIds.includes(normalizeId(issueType.id)),
      );

      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          issueTypes: selected,
        }),
      );
    },
    onSelectStatusesToLoad: (statusIds: string[]) => {
      const selected = statuses.filter((status) =>
        statusIds.includes(normalizeId(status.id)),
      );

      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          statusesToLoad: selected,
        }),
      );
    },
    onSelectDoneStatus: (statusId?: string) => {
      const selectedId = String(statusId || NONE_OPTION);
      const selectedStatus = statuses.find(
        (status) => normalizeId(status.id) === selectedId,
      );

      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          doneStatus: selectedId === NONE_OPTION ? undefined : selectedStatus,
        }),
      );
    },
    onToggleIncludeProjectInTitle: (value: boolean) => {
      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          includeProjectInTitle: value,
        }),
      );
    },
    onToggleIncludeTypeAcronymInTitle: (value: boolean) => {
      updateConnectionConfiguration<JiraConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          includeTypeAcronymInTitle: value,
        }),
      );
    },
  };
};
